import {Injectable} from "@angular/core";
import {HttpClient, HttpHeaders, HttpResponse} from "@angular/common/http";
import {catchError, map, Observable, of, throwError} from "rxjs";
import {StringIndex} from "../interfaces/generic/StringIndex";
import {GlobalConfig} from "../configs/GlobalConfig";
import {UserProfile} from "../interfaces/rest/UserProfile";
import {UserSessionRepository} from "../repositories/user-session.repository";
import {UserTokensRepository} from "../repositories/user-tokens.repository";
import {UserSession} from "../interfaces/domain/UserSession";
import {UserTokens} from "../interfaces/domain/UserTokens";
import {User} from "../interfaces/rest/User";
import {LocalStorageRepository} from "../repositories/local-storage.repository";
import {HttpRepository} from "../repositories/http.repository";
import {switchMap} from "rxjs/operators";
import {Helper} from "../helpers/Helper";

@Injectable()
export class AuthenticationService {

  constructor(private httpClient: HttpClient,
              private httpRepository: HttpRepository,
              private localStorageRepository: LocalStorageRepository,
              private userSessionRepository: UserSessionRepository,
              private userTokensRepository: UserTokensRepository) {

  }

  checkSsoByEmail(username: string): Observable<any> {
    if(Helper.isLocal()) {
      return of({ssoRequired: false});
    }

    username = encodeURIComponent(username);
    return this.httpClient.get(`${GlobalConfig.checkSsoEndpoint}?email=${username}`);
  }

  checkSsoByDomain(domain: string): Observable<any> {
    if(Helper.isLocal()) {
      return of({ssoRequired: false});
    }

    domain = encodeURIComponent(domain);
    return this.httpClient.get(`${GlobalConfig.checkSsoEndpoint}?domain=${domain}`);
  }

  loginWithSsoCode(ssoCode: string, email: string|null|undefined, domain: string|null|undefined): Observable<UserSession> {
    return new Observable((subscriber) => {
      const headers = this.emptyHeaders();
      (headers as StringIndex)['observe'] = 'response';

      let url = `${GlobalConfig.loginWithSsoCodeEndpoint}?code=${ssoCode}`;

      if(email && email.length > 0) {
        url = url + `&email=${email}`;
      }
      else if(domain && domain.length > 0) {
        url = url + `&domain=${domain}`;
      }

      this.httpClient.post<HttpResponse<any>>(url, '', headers).pipe(
        switchMap((response: HttpResponse<any>) => {
          const accessToken = response.headers.get('accessToken');

          if(!accessToken) {
            throw new Error('Missing token headers');
          }

          const userTokens: UserTokens = {
            accessToken: accessToken,
            refreshToken: '',
            sso: true,
          };

          this.userTokensRepository.setUserTokens(userTokens);

          return this.httpRepository.get<User>(GlobalConfig.endpoints.getCurrentUser);
        }),
        map((user) => {
          const userSession = this.userSessionRepository.createUserSession(user);
          return userSession;
        }),
        catchError((error) => {
          return this.processError(error);
        })
      ).subscribe({
        next: (result) => {
          subscriber.next(result);
        },
        error: (error) => {
          subscriber.error(error);
        },
        complete: () => {
          subscriber.complete();
        }
      });
    });
  }

  login(username: string, password: string, initialLogin = true): Observable<UserSession> {
    return new Observable((subscriber) => {
      const headers = this.authHeaderFromUserAndPassword(username, password);
      (headers as StringIndex)['observe'] = 'response';

      this.httpClient.post<HttpResponse<UserProfile>>(GlobalConfig.loginEndpoint, '', headers).pipe(
        map((response: HttpResponse<UserProfile>) => {
          return this.processUserProfileResponse(response);
        }),
        catchError((error) => {
          return this.processError(error);
        })
      ).subscribe({
        next: (result) => {
          subscriber.next(result);
        },
        error: (error) => {
          subscriber.error(error);
        },
        complete: () => {
          subscriber.complete();
        }
      });
    });
  }

  protected processUserProfileResponse(response: HttpResponse<UserProfile>): UserSession {
    const accessToken = response.headers.get('accessToken');
    const refreshToken = response.headers.get('refreshToken');
    const userProfile = response.body;

    if(!userProfile) {
      throw new Error('No user profile');
    }

    if(!accessToken || !refreshToken) {
      throw new Error('Missing token headers');
    }

    const userSession = new UserSession();
    userSession.userType = userProfile.isSee ? 'see' : 'subuser';
    userSession.email = userProfile.email;
    userSession.userId = userProfile.userId;
    userSession.roles = [userProfile.userRole];
    userSession.isPasswordPrivate = userProfile.isPasswordPrivate;
    userSession.agreement = userProfile.isTermsAndConditionsAccepted;
    userSession.avatarUrl = userProfile.profileImageUrl;
    userSession.clientLogo = userProfile.companyLogoUrl;
    userSession.languageId = userProfile.languageId;
    userSession.languageCode = userProfile.languageCode || 'en';
    userSession.clientId = userProfile.companyId;
    userSession.dateConfigObject = userProfile.dateFormat;
    userSession.timeConfigObject = userProfile.timeFormat;
    userSession.firstName = userProfile.firstName;
    userSession.lastName = userProfile.lastName;
    userSession.phoneNumber = userProfile.phoneNumber;

    this.userSessionRepository.setUserSession(userSession);

    const userTokens: UserTokens = {
      accessToken: accessToken,
      refreshToken: refreshToken
    };

    this.userTokensRepository.setUserTokens(userTokens);

    if(!userProfile.isPasswordPrivate) {
      this.userSessionRepository.setFirstLogin(true);
      this.userSessionRepository.setViewingTerms(false);
    }
    else if(userProfile.isResendWelcomeEmail) {
      this.userSessionRepository.setViewingTerms(true);
    }

    return userSession;
  }

  protected processError(error: any) {
    if(error && error.hasOwnProperty('status') && (error.status === 401 || error.status === 403)) {
      return throwError(() => new Error('Invalid user or password'));
    }
    else if(error && error.hasOwnProperty('status') && !error.message) {
      return throwError(() => new Error(`Server error (${error.status})`));
    }
    else {
      return throwError(() => error || new Error('Server error'));
    }
  }

  logout(): void {
    this.userSessionRepository.setUserSession(undefined);
    this.userSessionRepository.clearLocalStorage();
    this.userTokensRepository.clearLocalStorage();
    this.localStorageRepository.clearLocalStorage();
  }

  private authHeaderFromUserAndPassword(username: string, password: string) {
    let authBase64 = btoa(username + ':' + password);
    let headers = new HttpHeaders({'Authorization': `Basic ${authBase64}`});
    return {headers: headers};
  }

  private emptyHeaders() {
    const headers = new HttpHeaders({});
    return {headers: headers};
  }

  clearResetUID() {
    localStorage.removeItem('resetUID');
  }

  requestPasswordResetEmail(email: string) {
    return this.httpClient.post(`${GlobalConfig.endpoints.requestResetEmail}?email=${email}`, '');
  }

  resetPassword(email: string | undefined, token: string | undefined, newPassword: string | undefined) {
    let payload: User = new User();
    payload.email = email;
    payload.resetPasswordToken = token!.trim();
    payload.password = newPassword;

    return this.httpClient.post(GlobalConfig.endpoints.resetPassword, payload);
  }
}
